import React, { useEffect, useState } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { listAiVoices, addAiVoice, editAiVoice, deleteAiVoice, getProviderVoices } from "services/aiVoices"
import { getTierPrograms } from "services/tierProgram"
import { StyledAiAgent } from "components/Pages/AiAgent/AiAgent.style"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Chip from "@mui/material/Chip"
import {
  Edit as EditIcon,
  PowerSettingsNew as PowerIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import { verifyResponse } from "utils/httpRequest"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import TextField from "@mui/material/TextField"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import FormHelperText from "@mui/material/FormHelperText"
import { getProducts } from "services/products"

// Resolve voice tier from API (string "free"/"silver"/"gold"/"platinum" or tier program id) to tier program id for dropdown
const resolveTierForForm = (voiceTier, programs) => {
  const allTiers = programs || []
  const freeTier = allTiers.find((t) => String(t.type).toLowerCase() === "free")
  if (voiceTier === null || voiceTier === undefined || voiceTier === "") return freeTier ? freeTier.id : ""
  const num = Number(voiceTier)
  if (!Number.isNaN(num)) {
    const found = allTiers.find((t) => Number(t.id) === num)
    return found ? found.id : num
  }
  const lower = String(voiceTier).toLowerCase().trim()
  const byType = allTiers.find((t) => String(t.type).toLowerCase() === lower)
  return byType ? byType.id : (freeTier ? freeTier.id : "")
}

const parseMetadataObject = (metadata) => {
  if (!metadata) return null
  if (typeof metadata === "object") return metadata
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata)
      return parsed && typeof parsed === "object" ? parsed : null
    } catch {
      return null
    }
  }
  return null
}

const toNumberArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0)
  }
  if (value === null || value === undefined || value === "") return []
  if (typeof value === "string") {
    const parts = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    return parts
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0)
  }
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? [n] : []
}

const normalizeUniqueIds = (ids = []) =>
  [...new Set((ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))]

const getVoiceProductProgramIds = (voice) => {
  const metadata = parseMetadataObject(voice?.metadata)
  const fromDirectIds = toNumberArray(
    voice?.productProgramIds ??
      voice?.product_program_ids ??
      voice?.productGateIds ??
      voice?.product_gate_ids
  )
  const fromDirectSingle = toNumberArray(
    voice?.productProgramId ?? voice?.product_program_id ?? voice?.productGate
  )
  const fromMetaIds = toNumberArray(metadata?.productProgramIds ?? metadata?.product_program_ids)
  const fromMetaSingle = toNumberArray(metadata?.productProgramId ?? metadata?.product_program_id)
  return normalizeUniqueIds([
    ...fromDirectIds,
    ...fromDirectSingle,
    ...fromMetaIds,
    ...fromMetaSingle,
  ])
}

const AiVoices = () => {
  const [voices, setVoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [form, setForm] = useState({})
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [viewVoice, setViewVoice] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [providerVoices, setProviderVoices] = useState([])
  const [providerVoicesLoading, setProviderVoicesLoading] = useState(false)
  const [useManualProviderVoiceId, setUseManualProviderVoiceId] = useState(false)

  const nonProductTierPrograms = (tierPrograms || []).filter((t) => !(t && String(t.type).toLowerCase() === "product"))
  const freeTier = nonProductTierPrograms.find((t) => String(t.type).toLowerCase() === "free")
  const defaultTierId = freeTier ? freeTier.id : ""

  const AccessControl = UsePermissionVerification()

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]),
      addAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_ADD]),
      getDetailAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_DETAIL]),
      editAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_EDIT]),
      deleteAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_DELETE]),
      toggleStatusAiAgentAccess: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_TOGGLE_STATUS]),
      AI_AGENT_KNOWLEDGE_BASE_ADD: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_KNOWLEDGE_BASE_ADD]),
      AI_AGENT_KNOWLEDGE_BASE_DELETE: AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_KNOWLEDGE_BASE_DELETE]),
    })
  }, [AccessControl.UserPermissions])

  const loadVoices = async () => {
    setLoading(true)
    try {
      const resp = await listAiVoices({ page: 1, pageSize: 500 })
      verifyResponse(resp)
      const list = resp?.data?.list || resp?.data || []
      // Normalize incoming voice objects: map snake_case DB fields to camelCase used in UI
      const normalized = Array.isArray(list)
        ? list.map((v) => ({
            ...v,
            providerVoiceId: v.providerVoiceId || v.provider_voice_id || v.voiceId || v.voice_id || "",
            fallbackProviderVoiceId: v.fallbackProviderVoiceId || v.fallback_provider_voice_id || "",
          }))
        : []
      setVoices(normalized)
    } catch (err) {
      console.error("Error loading voices", err)
      setVoices([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadVoices()
  }, [])

  const loadProviderVoices = async (provider) => {
    const p = String(provider || "openai").toLowerCase()
    setProviderVoicesLoading(true)
    try {
      const resp = await getProviderVoices(p)
      verifyResponse(resp)
      const rows = resp?.data || resp || []
      setProviderVoices(Array.isArray(rows) ? rows : [])
    } catch (err) {
      console.warn("Failed to load provider voices", err)
      setProviderVoices([])
    } finally {
      setProviderVoicesLoading(false)
    }
  }

  useEffect(() => {
    if (!dialogOpen) return
    const provider = (form.provider || "openai").toLowerCase() === "frontend" ? "openai" : form.provider || "openai"
    loadProviderVoices(provider)
  }, [dialogOpen, form.provider])

  useEffect(() => {
    const loadAccessLookups = async () => {
      try {
        const [tierResp, productsResp] = await Promise.all([getTierPrograms(), getProducts()])
        const tierArr = tierResp?.data || tierResp
        setTierPrograms(Array.isArray(tierArr) ? tierArr : [])
        setProducts(Array.isArray(productsResp) ? productsResp : [])
      } catch {
        setTierPrograms([])
        setProducts([])
      }
    }
    loadAccessLookups()
  }, [])

  // Load provider voices when dialog opens or provider changes
  useEffect(() => {
    const loadProviderVoices = async () => {
      const provider = (form.provider || "").toLowerCase() === "frontend" ? "openai" : (form.provider || "openai")
      try {
        const resp = await getProviderVoices(provider)
        const voices = Array.isArray(resp) ? resp : (resp?.data ? resp.data : [])
        const mappedVoices = voices.map((v) => ({
          id: v.id || v.voice_id,
          name: v.name || v.id || v.voice_id,
        }))
        setProviderVoices((prev) => ({
          ...prev,
          [provider]: mappedVoices,
        }))
      } catch (err) {
        console.error("Error loading provider voices for", provider, err)
        setProviderVoices((prev) => ({
          ...prev,
          [provider]: [],
        }))
      }
    }

    // Load voices when dialog opens or provider/form changes
    if (dialogOpen && form.provider) {
      loadProviderVoices()
    }
  }, [dialogOpen, form.provider])

  const onAdd = async () => {
    setUseManualProviderVoiceId(false)
    setForm({
      id: null,
      name: "",
      provider: "openai",
      providerVoiceId: "",
      fallbackId: "",
      tier: "",
      productProgramIds: [],
      is_active: 1,
      locked: false,
      metadata: null,
    })
    setDialogOpen(true)
    // Load provider voices immediately
    try {
      const resp = await getProviderVoices("openai")
      const voices = Array.isArray(resp) ? resp : (resp?.data ? resp.data : [])
      const mappedVoices = voices.map((v) => ({
        id: v.id || v.voice_id,
        name: v.name || v.id || v.voice_id,
      }))
      setProviderVoices({ openai: mappedVoices })
    } catch (err) {
      console.error("Error loading OpenAI voices", err)
    }
  }

  const onEdit = (voice) => {
    setUseManualProviderVoiceId(false)
    const resolvedTier = resolveTierForForm(voice.tier ?? voice.productGate, tierPrograms)
    const provider = (voice.provider || "").toLowerCase() === "frontend" ? "openai" : (voice.provider || "openai")
    setForm({
      id: voice.id || voice._id,
      name: voice.name || voice.displayName || "",
      provider,
      providerVoiceId:
        voice.providerVoiceId || voice.provider_voice_id || voice.voiceId || voice.voice_id || "",
      fallbackId: voice.fallbackId || voice.fallback_id || "",
      tier: resolvedTier,
      productProgramIds: getVoiceProductProgramIds(voice),
      is_active: voice.is_active ?? voice.isActive ?? 1,
      locked: voice.locked || false,
      metadata: voice.metadata || null,
    })
    setDialogOpen(true)
  }

  const handleToggleStatus = async (voice) => {
    try {
      const payload = { id: voice.id, is_active: voice.is_active ? 0 : 1 }
      const resp = await editAiVoice(payload)
      verifyResponse(resp)
      loadVoices()
    } catch (err) {
      console.error(err)
    }
  }

  const confirmDeleteVoice = async () => {
    const targetId = deleteTarget?.id || deleteTarget?._id
    if (!targetId) return
    try {
      const resp = await deleteAiVoice(targetId)
      verifyResponse(resp)
      setDeleteTarget(null)
      loadVoices()
    } catch (err) {
      console.error(err)
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setForm({})
    setDialogLoading(false)
  }

  const handleFormChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const handleDialogSubmit = async () => {
    try {
      setDialogLoading(true)
      const productProgramIds = normalizeUniqueIds(form.productProgramIds || [])
      const resolvedTierId = form.tier || defaultTierId
      const providerVoiceId = form.providerVoiceId === "custom" ? form.customProviderVoiceId : form.providerVoiceId
      const payload = {
        id: form.id,
        name: form.name,
        provider: form.provider,
        providerVoiceId: form.providerVoiceId,
        fallbackId: form.fallbackId || null,
        tier: resolvedTierId,
        tierProgramId: resolvedTierId,
        productProgramIds,
        productProgramId: productProgramIds[0] || null,
        is_active: form.is_active ? 1 : 0,
        locked: form.locked ? 1 : 0,
        metadata: (() => {
          const existing = parseMetadataObject(form.metadata) || {}
          const hasGating = !!resolvedTierId || productProgramIds.length > 0
          if (!hasGating && Object.keys(existing).length === 0) return null
          return {
            ...existing,
            tierProgramId: resolvedTierId || null,
            productProgramId: productProgramIds[0] || null,
            productProgramIds,
          }
        })(),
      }
      let resp
      if (form.id) resp = await editAiVoice(payload)
      else resp = await addAiVoice(payload)
      verifyResponse(resp)
      handleDialogClose()
      loadVoices()
    } catch (err) {
      console.error(err)
      setDialogLoading(false)
    }
  }

  if (loading) return <LoadingScreen />

  return (
    <StyledAiAgent>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">AI Voices</h1>
          <p className="page-subtitle">
            Manage provider voice IDs, tier availability, and activation status.
          </p>
        </div>
        <div className="actions">
          {AccessControl.Access.addAiAgentAccess && (
            <Button
              className="new-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={onAdd}
            >
              Add Voice
            </Button>
          )}
        </div>
      </PageHeading>

      <div className="page-surface">
        {AccessControl.Access.listAiAgentAccess && (
          <div className="table-container">
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Provider Voice ID</TableCell>
                    <TableCell>Fallback Provider ID</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Product Gates</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(voices || []).length === 0 && (
                    <TableRow>
                      <TableCell className="table-empty" colSpan={8}>
                        No voices found.
                      </TableCell>
                    </TableRow>
                  )}
                  {(voices || []).map((v) => (
                    <TableRow key={v.id || v._id || `${v.provider}-${v.providerVoiceId}`}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {v.name || v.displayName || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{v.provider || "-"}</TableCell>
                      <TableCell>{v.providerVoiceId || v.voiceId || "-"}</TableCell>
                      <TableCell>{v.fallbackId || v.fallback_id || "-"}</TableCell>
                      <TableCell>
                        {(() => {
                          const t =
                            (tierPrograms || []).find((x) => String(x.id) === String(v.tier)) ||
                            (tierPrograms || []).find(
                              (x) =>
                                String(x.type).toLowerCase() ===
                                String(v.tier || v.productGate || "").toLowerCase()
                            )
                          if (t)
                            return (
                              t.name ||
                              (t.type
                                ? String(t.type).charAt(0).toUpperCase() +
                                  String(t.type).slice(1)
                                : `#${t.id}`)
                            )
                          return v.tier || v.productGate || "-"
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const ids = getVoiceProductProgramIds(v)
                          if (!ids.length) return "-"
                          return ids
                            .map((id) => {
                              const product = (products || []).find(
                                (p) => Number(p.programId) === Number(id)
                              )
                              return product?.name || product?.code || `#${id}`
                            })
                            .join(", ")
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={v.is_active || v.isActive ? "Active" : "Inactive"}
                          className={`status-badge ${
                            v.is_active || v.isActive
                              ? "status-active"
                              : "status-inactive"
                          }`}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <div className="action-buttons" style={{ justifyContent: "flex-end" }}>
                          {AccessControl.Access.getDetailAiAgentAccess && (
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => setViewVoice(v)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {AccessControl.Access.editAiAgentAccess && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => onEdit(v)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {AccessControl.Access.toggleStatusAiAgentAccess && (
                            <Tooltip title="Toggle Status">
                              <IconButton
                                size="small"
                                onClick={() => handleToggleStatus(v)}
                              >
                                <PowerIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {AccessControl.Access.deleteAiAgentAccess && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(v)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        {!AccessControl.Access.listAiAgentAccess && (
          <div className="page-empty">
            You do not have permission to view voice records.
          </div>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        PaperProps={{
          sx: {
            width: "min(700px, calc(100vw - 28px))",
            borderRadius: "16px",
            border: "1px solid var(--cms-border)",
            background: "var(--cms-dialog-bg)",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            "& .dialog-title-group": { display: "flex", flexDirection: "column", gap: "5px" },
            "& .dialog-title": {
              margin: 0,
              fontSize: "20px",
              fontWeight: 800,
              color: "var(--cms-text-primary)",
              letterSpacing: "-0.01em",
            },
            "& .dialog-subtitle": {
              margin: 0,
              fontSize: "13px",
              color: "var(--cms-text-secondary)",
              fontWeight: 500,
            },
          }}
        >
          <div className="dialog-title-group">
            <h2 className="dialog-title">{form?.id ? "Edit Voice" : "Add Voice"}</h2>
            <p className="dialog-subtitle">
              Configure provider mapping and tier-level availability.
            </p>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name || ""}
              onChange={(e) => handleFormChange("name", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={
                  (form.provider || "openai").toLowerCase() === "frontend"
                    ? "openai"
                    : form.provider || "openai"
                }
                label="Provider"
                onChange={(e) => {
                  handleFormChange("provider", e.target.value)
                  handleFormChange("providerVoiceId", "")
                  setUseManualProviderVoiceId(false)
                }}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={useManualProviderVoiceId}
                  onChange={(e) => setUseManualProviderVoiceId(e.target.checked)}
                />
              }
              label="Enter provider voice ID manually"
            />
            {useManualProviderVoiceId ? (
              <TextField
                label="Provider Voice ID"
                value={form.providerVoiceId || ""}
                onChange={(e) => handleFormChange("providerVoiceId", e.target.value)}
                fullWidth
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel id="provider-voice-select-label">Provider Voice</InputLabel>
                <Select
                  labelId="provider-voice-select-label"
                  label="Provider Voice"
                  value={form.providerVoiceId || ""}
                  onChange={(e) => handleFormChange("providerVoiceId", e.target.value)}
                  disabled={providerVoicesLoading}
                >
                  {(providerVoices || []).map((v) => (
                    <MenuItem key={`pv-${v.id}`} value={v.id}>
                      {v.name ? `${v.name} (${v.id})` : v.id}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {providerVoicesLoading
                    ? "Loading provider voices…"
                    : "Fetched live from OpenAI (curated) or ElevenLabs API."}
                </FormHelperText>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel id="fallback-provider-voice-label">Fallback Provider ID</InputLabel>
              <Select
                labelId="fallback-provider-voice-label"
                label="Fallback Provider ID"
                value={form.fallbackId || ""}
                onChange={(e) => handleFormChange("fallbackId", e.target.value)}
                disabled={providerVoicesLoading}
              >
                <MenuItem value="">None</MenuItem>
                {(providerVoices || []).map((v) => (
                  <MenuItem key={`fpv-${v.id}`} value={v.id}>
                    {v.name ? `${v.name} (${v.id})` : v.id}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {providerVoicesLoading
                  ? "Loading provider voices…"
                  : "Select a fallback OpenAI voice as a backup option."}
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={
                  form.tier !== undefined && form.tier !== "" ? form.tier : defaultTierId
                }
                label="Tier"
                onChange={(e) => handleFormChange("tier", e.target.value)}
              >
                {[...(tierPrograms || [])]
                  .sort((a, b) => {
                    const order = { free: 0, silver: 1, gold: 2, platinum: 3, product: 4 }
                    const aIdx = order[String(a?.type || "").toLowerCase()] ?? 99
                    const bIdx = order[String(b?.type || "").toLowerCase()] ?? 99
                    return aIdx - bIdx
                  })
                  .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {String(t?.type || "").toLowerCase() === "product"
                      ? "Product (select below)"
                      : t && (t.name || t.type)
                      ? t.name ||
                        (String(t.type).charAt(0).toUpperCase() +
                          String(t.type).slice(1))
                      : `#${t.id}`}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select one tier. Product gates can also be selected below.
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="voice-product-gate-select-label">Product Gate</InputLabel>
              <Select
                multiple
                labelId="voice-product-gate-select-label"
                value={Array.isArray(form.productProgramIds) ? form.productProgramIds : []}
                label="Product Gate"
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected : []
                  if (!ids.length) return "None"
                  return ids
                    .map((id) => {
                      const product = (products || []).find(
                        (p) => Number(p.programId) === Number(id)
                      )
                      return product?.name || product?.code || `#${id}`
                    })
                    .join(", ")
                }}
                onChange={(e) => {
                  const value = e.target.value
                  const next = Array.isArray(value) ? value : (value ? [value] : [])
                  handleFormChange("productProgramIds", normalizeUniqueIds(next))
                }}
              >
                {Array.isArray(products) && products.length > 0 ? (
                  products.map((p) => (
                    <MenuItem key={p.programId} value={p.programId}>
                      <Checkbox
                        checked={
                          Array.isArray(form.productProgramIds) &&
                          form.productProgramIds.includes(Number(p.programId))
                        }
                      />
                      <ListItemText primary={p.name || p.code || `#${p.programId}`} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No products available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) =>
                    handleFormChange("is_active", e.target.checked ? 1 : 0)
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            disabled={dialogLoading}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={dialogLoading}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            {dialogLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(viewVoice)}
        onClose={() => setViewVoice(null)}
        PaperProps={{
          sx: {
            width: "min(620px, calc(100vw - 28px))",
            borderRadius: "16px",
            border: "1px solid var(--cms-border)",
            background: "var(--cms-dialog-bg)",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800 }}>Voice Details</DialogTitle>
        <DialogContent>
          {viewVoice && (
            <Box sx={{ display: "grid", gap: 1.25, mt: 0.5 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {viewVoice.name || viewVoice.displayName || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Provider:</strong> {viewVoice.provider || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Provider Voice ID:</strong>{" "}
                {viewVoice.providerVoiceId || viewVoice.voiceId || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Fallback Provider ID:</strong>{" "}
                {viewVoice.fallbackId || viewVoice.fallback_id || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Tier:</strong> {viewVoice.tier || viewVoice.productGate || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Product Gates:</strong>{" "}
                {(() => {
                  const ids = getVoiceProductProgramIds(viewVoice)
                  if (!ids.length) return "-"
                  return ids
                    .map((id) => {
                      const product = (products || []).find(
                        (p) => Number(p.programId) === Number(id)
                      )
                      return product?.name || product?.code || `#${id}`
                    })
                    .join(", ")
                })()}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong>{" "}
                {viewVoice.is_active || viewVoice.isActive ? "Active" : "Inactive"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setViewVoice(null)}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            width: "min(520px, calc(100vw - 28px))",
            borderRadius: "16px",
            border: "1px solid var(--cms-border)",
            background: "var(--cms-dialog-bg)",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, color: "var(--cms-text-primary)" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete voice "{deleteTarget?.name || deleteTarget?.providerVoiceId}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteVoice}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </StyledAiAgent>
  )
}

export default AiVoices
